import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getJobStatus, getJobLogs } from '@/api/jobs';
import { RFDiffusionResults } from '../JobResults/RFDiffusionResults';
import { RFDiffusionBinderResults } from '../JobResults/RFDiffusionBinderResults';
import { ESMIFResults } from '../JobResults/ESMIFResults';
import { StructureViewer } from '../JobResults/StructureViewer';
import { FileList } from '../JobResults/FileList';
import { ColabfoldResults } from '../JobResults/ColabfoldResults';
import { OptimizationResults } from '../JobResults/OptimizationResults';
import { ModelType } from '@/types/colabfold';

interface JobDetailProps {
  jobId: string;
  modelType: ModelType;
}

export const JobDetail: React.FC<JobDetailProps> = ({ jobId, modelType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPdbFile, setSelectedPdbFile] = useState<string | null>(null);

  // Parse query parameters to get tab and file
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const file = searchParams.get('file');
    
    if (tab) {
      const tabIndex = parseInt(tab);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        setActiveTab(tabIndex);
      }
    }
    
    if (file) {
      setSelectedPdbFile(file);
    }
  }, [location.search]);

  const { data: jobDetails, isLoading, error, refetch } = useQuery({
    queryKey: ['jobDetails', jobId, modelType],
    queryFn: () => getJobStatus(modelType, jobId),
    refetchInterval: (data) => {
      const status = data?.status?.toLowerCase();
      return status === 'completed' || status === 'failed' ? false : 5000;
    },
    enabled: !!jobId
  });

  const { data: logsData } = useQuery({
    queryKey: ['jobLogs', jobId, modelType],
    queryFn: () => getJobLogs(modelType, jobId),
    refetchInterval: () => {
      const status = jobDetails?.status?.toLowerCase();
      return status === 'completed' || status === 'failed' ? false : 5000;
    },
    enabled: !!jobId && activeTab === 3
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading job details</Typography>
      </Box>
    );
  }

  if (!jobDetails) return null;

  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      default: return 'default';
    }
  };

  const getModelTypeLabel = (type: string) => {
    switch (type) {
      case ModelType.COLABFOLD: return 'ColabFold';
      case ModelType.RFDIFFUSION: return 'RF Diffusion';
      case ModelType.ESMIF: return 'ESM-IF';
      case ModelType.PROTEINOPT: return 'Protein Optimization';
      default: return type;
    }
  };

  // Helper to get PDB file
  const getPdbFile = () => {
    // First check if there's a selected PDB file from the URL
    if (selectedPdbFile) {
      return selectedPdbFile;
    }
    
    if (modelType === ModelType.RFDIFFUSION) {
      return 'output_0.pdb';
    }
    
    if (modelType === ModelType.ESMIF) {
      // For ESM-IF, we want to show the input PDB structure
      // The input PDB file should be named input.pdb in the job files
      return 'input.pdb';
    }
    
    if (modelType === ModelType.PROTEINOPT) {
      // For protein optimization, look for the input PDB file
      // It could be named input.pdb or the original filename
      const files = getOutputFiles();
      const pdbFile = files.find((file: string) => 
        file.endsWith('.pdb') && (file.includes('input') || !file.includes('output'))
      );
      return pdbFile || 'input.pdb';
    }
    
    // For other models, use ranked model
    if (jobDetails.instance_status?.quality_metrics?.ranked_model) {
      return jobDetails.instance_status.quality_metrics.ranked_model;
    }
    
    return '';
  };

  // Helper to get output files
  const getOutputFiles = () => {
    if (jobDetails.instance_status?.results?.files) {
      return jobDetails.instance_status.results.files;
    }
    return [];
  };

  // Render results tab content based on model type
  const renderResultsContent = () => {
    const status = jobDetails.status.toLowerCase();
    
    // For RFDiffusion, determine the design mode
    const design_mode = jobDetails.instance_status?.results?.design_mode || 
                       jobDetails.instance_status?.results?.params?.design_mode || 
                       'general';
    
    // Get quality metrics for Colabfold results
    const qualityMetrics = jobDetails.instance_status?.quality_metrics || {};

    switch (modelType) {
      case ModelType.RFDIFFUSION:
        // Conditionally render the appropriate RFDiffusion results component based on mode
        if (design_mode === 'binder' || jobDetails.instance_status?.results?.binder_design === true) {
          return (
            <RFDiffusionBinderResults 
              results={jobDetails.instance_status?.results || {}} 
            />
          );
        } else {
          return (
            <RFDiffusionResults 
              results={jobDetails.instance_status?.results || {}} 
              jobId={jobId} 
            />
          );
        }
      case ModelType.COLABFOLD:
        return (
          <ColabfoldResults 
            results={{ ...jobDetails.instance_status?.results, ...jobDetails.input_params }} 
            qualityMetrics={qualityMetrics}
          />
        );
      case ModelType.ESMIF:
        return (
          <ESMIFResults
            results={jobDetails.instance_status?.results || {}}
            jobId={jobId}
          />
        );
      case ModelType.PROTEINOPT:
        return (
          <OptimizationResults
            results={{ ...jobDetails.instance_status?.results, ...jobDetails.input_params }}
            jobId={jobId}
          />
        );
      default:
        return (
          <Typography>
            {status === 'completed' ? 'Job completed successfully. View 3D structure or files tabs for results.' : 'Job failed. Check logs for details.'}
          </Typography>
        );
    }
  };

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header with back button and job name */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            {jobDetails.instance_status?.jobname || `Job ${jobId}`}
          </Typography>
          <Chip 
            label={getModelTypeLabel(modelType)} 
            color="primary" 
            variant="outlined" 
            sx={{ mr: 1 }}
          />
          <Chip 
            label={jobDetails.status} 
            color={getStatusColor(jobDetails.status)}
            sx={{ mr: 1 }}
          />
          <IconButton onClick={() => refetch()}>
            <Refresh />
          </IconButton>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Results" />
            <Tab label="3D Structure" />
            <Tab label="Files" />
            <Tab label="Logs" />
          </Tabs>

          {activeTab === 0 && renderResultsContent()}
          
          {activeTab === 1 && (
            <StructureViewer
              jobId={jobId}
              modelType={modelType}
              pdbFile={getPdbFile()}
            />
          )}
          
          {activeTab === 2 && (
            <FileList
              files={getOutputFiles()}
              jobId={jobId}
              modelType={modelType}
              jobName={jobDetails.instance_status?.jobname || `Job ${jobId}`}
              collapsible={true}
            />
          )}

          {activeTab === 3 && (
            <Box sx={{ p: 2 }}>
              {logsData?.logs ? (
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {logsData.logs}
                </pre>
              ) : (
                <Alert severity="info">
                  <AlertTitle>No Logs Available</AlertTitle>
                  Logs are not available for this job. This could be because:
                  <ul>
                    <li>The job is still in progress</li>
                    <li>The logs were not generated</li>
                    <li>The logs have been cleaned up</li>
                  </ul>
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default JobDetail; 